/**
 * Fire Life Safety — the block header the §3.11 questions sit under, and the
 * photo prompts that are the actual point of them (spec Amendment 5, D-024).
 *
 * ── THE PHOTOS ARE THE PRODUCT ───────────────────────────────────────────────
 * Every other block in this survey exists so the office can price OSB's own
 * work. This one exists so an OUTSIDE fire-alarm company can price theirs, off
 * a packet they never visit the building to check. A tidy column of Y/N answers
 * with no pictures is close to worthless to them; plain answers with a clear
 * photograph of the panel and its service sticker is a quote.
 *
 * So the camera comes FIRST here — above the questions, in the same block, with
 * a sentence per shot saying what has to be in the frame — rather than waiting
 * at the bottom of the screen in the close panel that used to sit there, where
 * a surveyor who had already answered everything had stopped reading by the
 * time they reached it (that panel is deleted now — D-033 R16). That is the
 * one thing this component adds; everything else on screen is the ordinary
 * question renderer.
 *
 * ── Why a component, and where the block lives ───────────────────────────────
 * FLS is not one place (catalog.ts says so at length): it is a panel in a lobby,
 * two relays in a machine room, a sprinkler in a shaft, three devices in a pit
 * and a smoke detector at twenty landings. The per-room, per-hoistway and
 * per-group questions live in the section that repeats at their own level and
 * arrive here already ordered last within it — which is what lets this be a
 * header dropped in front of the first of them rather than a second renderer.
 * The panel and its paperwork (F1–F5) ARE one place, and since Amendment 6
 * (2026-08-18) they have a section of their own — `fls`, at the end of the walk
 * — where this block is the whole body. On that section the map bar's line
 * already says "Fire Life Safety" (D-033 R12 — no section prints a title of
 * its own), so the block's own eyebrow is held back there rather than said
 * twice.
 *
 * ── The close panel is gone; there is nothing left to duplicate ─────────────
 * First cut rendered these rows in both places, on the honest-looking argument
 * that the ad hoc checklist is shown twice too (D-023). It was not the same
 * thing: ad hoc appears once on its own section and again on the way OUT, on a
 * different screen. Two identical "Fire service relays · Not taken yet" rows
 * twenty lines apart on ONE screen was a surveyor working out whether they
 * owed the office one photo or two — and `tests/e2e/offline.spec.ts` caught it
 * immediately, because one attach stopped turning exactly one row green. The
 * fix, back then, was this block owning its rows and the close panel not
 * repeating them.
 *
 * Since D-028 (2026-08-30) the cameras are the photo shelf at the top of every
 * section — one card per shot, the FLS shots among them with their framing
 * notes in the viewer — so this block is the header in front of the first
 * F-question that has a surface (F1, derived-only, has none — D-033 R14). And
 * since D-033 R16 deleted the close panel outright, the duplication this
 * section used to guard against has no second surface left to happen on.
 */

import type { ReactNode } from 'react';

import type { SectionInstance } from '../../core/types';
import { Eyebrow } from './Surfaces';

export interface FlsBlockProps {
  section: SectionInstance;
}

export function FlsBlock({ section }: FlsBlockProps): ReactNode {
  // On the section that IS Fire Life Safety there is nothing left to head:
  // the map bar names it, and an empty region is noise.
  if (section.kind === 'fls') return null;
  return (
    <section className="flsblock" aria-label={`Fire and life safety · ${section.title}`}>
      <Eyebrow>Fire &amp; life safety</Eyebrow>

    </section>
  );
}
